<script setup lang="ts">
import { formatNumberFullTruncate } from '@/helpers/utils'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import ThePremium from '@/components/ThePremium.vue'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import type { Friend } from '@/types/referral'
import { T, useTranslate } from '@tolgee/vue'
import { computed } from 'vue'
import Clock3Icon from '@/components/Clock3Icon.vue'
import { formatDuration } from '@/helpers/date'
import VirtualFriendImage from '@/assets/images/gifs/duck-love.gif'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import { useReferredFriends } from '@/composables/game/referred-friends'

const props = defineProps<{
  friend: Friend
  isPoking: boolean
  poked: boolean
}>()

defineEmits<{
  (e: 'onChallenge'): void
  (e: 'onPoke'): void
}>()
const { t } = useTranslate()

const { pokeLimit, remainingTimeToPoke } = useReferredFriends()

const isPokeBlocked = computed(() => (remainingTimeToPoke.value > 0))
const isReferred = computed(() => props.friend.referralGain !== null)
const tooltipText = t.value('friends.poke.tooltip', `You can poke them again\nafter they poke you back\nor after 24 hours.`)

const friendAvatar = computed(() => {
  if (props.friend.isVirtual) {
    return VirtualFriendImage
  }
  return props.friend.photoUrl ?? ''
})
</script>
<template>
  <div
    class="flex bg-dark-bottom-layer w-full p-2 pr-4 rounded-xl justify-between items-center gap-x-2"
  >
    <div class="flex gap-x-[14px] items-center min-w-0 flex-1">
      <PlayerAvatar
        class="size-12 min-w-[48px]"
        :image-src="friendAvatar"
        :first-name="friend.firstName"
        @click="$emit('onChallenge')"
      />
      <div class="flex flex-col min-w-0 gap-y-0.5">
        <div class="flex items-center min-w-0">
          <p class="text-base font-semibold tracking-tight truncate">
            {{ friend.firstName }}
          </p>
          <ThePremium
            class="ml-1"
            v-if="friend.isPremium"
          />
          <div
            v-if="isReferred"
            class="flex justify-center items-center rounded-full px-1 h-4 w-max min-w-fit bg-white/[38%] ml-2"
          >
            <span class="text-xs font-medium text-dark-bottom-layer">
              <T
                key-name="friends.referred"
                default-value="referred"
              />
            </span>
          </div>
          <div
            v-else-if="friend.isVirtual"
            class="flex justify-center items-center rounded-full px-1 h-4 w-max min-w-fit bg-white/[38%] ml-2"
          >
            <span class="text-xs font-medium text-dark-bottom-layer">
              <T
                key-name="friends.virtual"
                default-value="virtual"
              />
            </span>
          </div>
        </div>
        <div
          v-if="!friend.isVirtual"
          class="max-h-[24px] flex gap-x-3 items-center font-semibold min-w-0"
        >
          <p class="text-xs leading-6 font-medium -tracking-[0.24px] text-nowrap">
            <T
              key-name="levels.level"
              default-value="Level {index}"
              :params="{
                index: friend.level
              }"
            />
          </p>
          <div class="w-[1px] h-4 bg-white/20" />
          <NumberFormatTooltip
            :number="friend.profit"
            :formatting-type="'full'"
          >
            <div
              class="flex items-center gap-x-1"
            >
              <img
                :src="CoinIcon"
                class="size-4"
                alt="Coin Icon"
              >
              <span class="font-semibold text-xs leading-6 text-solid-white -tracking-[0.24px]">{{
                formatNumberFullTruncate(friend.profit)
              }} </span>
            </div>
          </NumberFormatTooltip>
        </div>
      </div>
    </div>
    <VTooltip
      :disabled="!poked && !isPokeBlocked"
      placement="left"
      :triggers="['touch', 'click', 'hover']"
      :popper-triggers="['touch', 'click', 'hover']"
    >
      <template #popper>
        <p
          v-if="poked"
          class="whitespace-pre-line"
        >
          {{ tooltipText }}
        </p>
        <div
          v-else
          class="flex flex-col gap-y-1 whitespace-pre-line"
        >
          <p>
            <T
              key-name="friends.poke_limit"
              :default-value="`You can poke ${pokeLimit} times\nevery 10 minutes.`"
            />
          </p>
          <div class="flex items-center gap-x-1">
            <Clock3Icon class="size-4 text-white" />
            <span
              class="text-teal-primary text-base font-medium"
            >{{ formatDuration(remainingTimeToPoke, 'mm:ss') }}</span>
            <T
              key-name="friends.left"
              default-value="left"
            />
          </div>
        </div>
      </template>
      <div
        class="flex items-center py-1 px-1.5 gap-x-1 bg-white/[.12] active:bg-white/[.24] rounded-[100px]"
        :class="{'opacity-[32%]': poked}"
        @click="!poked && !isPokeBlocked && $emit('onPoke')"
      >
        <SpinnerIcon
          v-if="isPoking"
          class="w-5 h-5 min-w-[20px]"
        />
        <img
          v-else
          src="@/assets/images/gifs/poke.gif"
          class="size-5 min-w-[20px]"
        >
        <span class="poke-text-shadow text-white uppercase text-xs leading-3 font-bold">
          <T
            key-name="friends.poke"
            default-value="Poke"
          />
        </span>
      </div>
    </VTooltip>
  </div>
</template>